/**
 * TemplateFormBuilderPage — hub Kreatora, Krok 2: projektowanie układu formularza
 * (widok `form`) encji szablonu w scope ADMIN, bez SQL.
 *
 * Reużywa cały canvas `ModuleBuilderPage` przez `ModuleBuilderAdapter` w trybie
 * `template`:
 *   - dane: definicja szablonu z ADM (pola encji → pola buildera, `fieldId` = slug,
 *     typy mapowane przez `TEMPLATE_TO_BUILDER_FIELD_TYPE`), istniejący `formConfig`
 *     → sekcje buildera (lustro translatora instalacji z Kroku 1),
 *   - zapis NA ŻĄDANIE: sekcje → `formConfig` (DOKŁADNA odwrotność translatora —
 *     `clientSectionsToTemplateFormConfig` z @saas/shared) → PATCH
 *     `.../views/[viewSlug]/config` (szew z Kroku 0: bump wersji + snapshot,
 *     dedup no-op, 409 NO_STRUCTURE / VERSION_CONFLICT),
 *   - byty tenanta wyłączone: LayoutSwitcher, kategorie, role, panel „Ustawienia",
 *     dodawanie/usuwanie pól (struktura żyje w edytorze szablonu).
 *
 * Routing: `/module-templates/:slug/entities/:entitySlug/views/:viewSlug/form-builder`.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  clientSectionsToTemplateFormConfig,
  identityFieldMap,
  templateFormConfigToClientSections,
} from '@saas/shared';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import { Badge } from '@/components/Badge';
import type { ModuleField } from '@/components/fields/FieldRegistry';
import { Layout } from '@/components/Layout';
import {
  extractLayoutSingletonTypes,
  layoutSingletonRejectedMessage,
} from '@/lib/layout-singleton-error';
import {
  extractTemplateApiErrorCode,
  patchTemplateViewConfig,
  TEMPLATE_TO_BUILDER_FIELD_TYPE,
  useModuleTemplateDetail,
} from '@/lib/module-templates/template-layouts';

import type {
  BuilderModuleDetail,
  FormLayoutTab,
  LayoutSection,
  ModuleBuilderAdapter,
} from './modules/module-builder-adapter';
import { ModuleBuilderPage } from './modules/ModuleBuilderPage';

interface TemplateFormBuilderContext {
  adapter: ModuleBuilderAdapter;
  templateName: string | undefined;
  entityName: string | undefined;
  viewName: string | undefined;
}

/**
 * Domyślny układ formularza dla świeżego widoku (brak zapisanego formConfig): jedna
 * sekcja ze WSZYSTKIMI polami encji (wg sortOrder z zakładki „Pola"). Dzięki temu
 * projektant nie zaczyna od pustego płótna — usuwa/przestawia gotowe pola. (Sprawa 1b)
 */
function buildDefaultFormSections(
  fields: { slug: string; name: string; isRequired: boolean; sortOrder: number }[],
): LayoutSection[] {
  const sorted = [...fields].sort((a, b) => a.sortOrder - b.sortOrder);
  return [
    {
      id: 'section-default',
      title: null,
      columns: 1,
      showHeader: false,
      showBorder: false,
      collapsible: false,
      elements: sorted.map((f) => ({
        id: `el-${f.slug}`,
        type: 'field',
        fieldId: f.slug,
        width: 1,
        settings: { label: f.name, required: f.isRequired },
      })),
    },
  ];
}

/**
 * Adapter trybu `template` dla ModuleBuilderPage. Pola adresowane slugiem w obie
 * strony (mapa identycznościowa) — dopiero instalacja u klienta tłumaczy slug→fieldId.
 */
function useTemplateFormBuilderAdapter(
  code: string,
  entitySlug: string,
  viewSlug: string,
): TemplateFormBuilderContext {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const detailQuery = useModuleTemplateDetail(code);

  const derived = useMemo(() => {
    const tpl = detailQuery.data;
    if (!tpl) return undefined;
    const entity = tpl.entities.find((e) => e.slug === entitySlug);
    const view = entity?.views.find((v) => v.slug === viewSlug && v.type === 'form');
    if (!entity || !view) return { broken: true as const };

    const fieldSlugs = entity.fields.map((f) => f.slug);
    let sections: LayoutSection[];
    let tabs: FormLayoutTab[];
    try {
      if (view.formConfig) {
        // Kształty są zgodne strukturalnie (settings/style to szersze rekordy) — patrz
        // kontrakt w module-builder-form-layout.ts; rzutowanie zamyka różnicę typów.
        const translated = templateFormConfigToClientSections(
          view.formConfig,
          identityFieldMap(fieldSlugs),
        );
        sections = translated.sections as unknown as LayoutSection[];
        tabs = translated.tabs;
      } else {
        // Brak zapisanego układu (świeży widok formularza) → zamiast pustego płótna
        // seedujemy sekcję ze WSZYSTKIMI polami encji (wg sortOrder z zakładki „Pola").
        // Wdrożeniowiec usuwa/przestawia; zapis utrwala formConfig. (Sprawa 1b 2026-06-25)
        sections = buildDefaultFormSections(entity.fields);
        tabs = [];
      }
    } catch {
      // Zepsuty formConfig (slug spoza pól encji) — walidator struktury to wyklucza;
      // zamiast cicho nadpisać układ klienta pustym, pokazujemy stan błędu.
      return { broken: true as const };
    }

    const fields: ModuleField[] = [...entity.fields]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((f) => ({
        id: f.slug, // builder adresuje pola po `id` — w hubie id ≡ slug
        moduleId: `template:${code}:${entitySlug}`,
        name: f.name,
        slug: f.slug,
        description: f.description ?? null,
        fieldType: TEMPLATE_TO_BUILDER_FIELD_TYPE[f.type] ?? f.type,
        isRequired: f.isRequired,
        isUnique: false,
        isSearchable: false,
        isFilterable: false,
        showInList: true,
        defaultValue: f.defaultValue ?? null,
      }));

    const module: BuilderModuleDetail = {
      id: `template:${code}:${entitySlug}`,
      slug: entitySlug,
      name: `${tpl.name} · ${entity.name}`,
      fields,
      layouts: [
        {
          id: `template-view:${viewSlug}`,
          moduleId: `template:${code}:${entitySlug}`,
          layoutType: 'form',
          name: view.name,
          isDefault: true,
          // Krok 8 — rejestr zakładek przechodzi do buildera (pusty = płaski układ).
          layoutConfig: { sections, ...(tabs.length > 0 ? { tabs } : {}) },
        },
      ],
    };
    return { module, templateName: tpl.name, entityName: entity.name, viewName: view.name };
  }, [detailQuery.data, code, entitySlug, viewSlug]);

  const adapter: ModuleBuilderAdapter = {
    mode: 'template',
    isLoading: detailQuery.isLoading,
    isError: detailQuery.isError || derived?.broken === true,
    module: derived && !derived.broken ? derived.module : undefined,
    categories: [],
    roleTemplates: undefined,
    saveLayout: async (_layoutId, config) => {
      const tpl = detailQuery.data;
      const entity = tpl?.entities.find((e) => e.slug === entitySlug);
      if (!entity) throw new Error('Template entity not loaded');
      const formConfig = clientSectionsToTemplateFormConfig(
        config.sections,
        identityFieldMap(entity.fields.map((f) => f.slug)),
        // Krok 8 — zakładki buildera wracają do kontraktu (root type='tabs').
        config.tabs,
      );
      await patchTemplateViewConfig(code, entitySlug, viewSlug, { formConfig });
    },
    saveErrorMessage: (err) => {
      // Reguła „jeden blok na layout" (pozycje dokumentu, rejestr VAT) — komunikat z nazwami typów.
      const singletonTypes = extractLayoutSingletonTypes(err);
      if (singletonTypes) return layoutSingletonRejectedMessage(t, singletonTypes);
      const errorCode = extractTemplateApiErrorCode(err);
      if (errorCode === 'VERSION_CONFLICT') {
        return t('moduleTemplateEditor.layoutDesigner.errors.versionConflict');
      }
      if (errorCode === 'NO_STRUCTURE') {
        return t('moduleTemplateEditor.layoutDesigner.errors.noStructure');
      }
      if (errorCode === 'VALIDATION_FAILED') {
        return t('moduleTemplateEditor.layoutDesigner.errors.validation');
      }
      return undefined;
    },
    invalidate: () => queryClient.invalidateQueries({ queryKey: ['admin', 'module-templates'] }),
  };

  return {
    adapter,
    templateName: derived && !derived.broken ? derived.templateName : undefined,
    entityName: derived && !derived.broken ? derived.entityName : undefined,
    viewName: derived && !derived.broken ? derived.viewName : undefined,
  };
}

export function TemplateFormBuilderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    slug = '',
    entitySlug = '',
    viewSlug = '',
  } = useParams<{ slug: string; entitySlug: string; viewSlug: string }>();

  const { adapter, templateName, entityName, viewName } = useTemplateFormBuilderAdapter(
    slug,
    entitySlug,
    viewSlug,
  );

  return (
    <Layout>
      <section className="mx-auto max-w-7xl px-6 py-6" data-testid="template-form-builder-page">
        <div className="mb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/module-templates/${slug}`)}
            className="rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            aria-label={t('moduleTemplateEditor.layoutDesigner.back')}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold">
              {t('moduleTemplateEditor.layoutDesigner.formTitle')}
            </h1>
            <div className="mt-0.5 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              {templateName && entityName ? (
                <span>
                  {templateName} · {entityName}
                </span>
              ) : (
                <code>
                  {slug}/{entitySlug}
                </code>
              )}
              {viewName && <Badge color="blue">{viewName}</Badge>}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <ModuleBuilderPage embedded adapter={adapter} />
        </div>
      </section>
    </Layout>
  );
}
